import React, { useState, useEffect } from 'react'
import { useMutation } from '@apollo/client'
import UpdateAvatarModal from '../UpdateAvatarModal'
import {
  TorneioProfile,
  Organizador,
  atualizarAvatarTorneio,
  uploadBannerTorneio,
  uploadBannerVTorneio,
  atualizarTorneio,
  criarTorneio,
} from '../../services/torneioService'
import { useAuth } from '../../context/AuthContext'
import {
  CRIAR_SEGUIDOR_MUTATION,
  DELETAR_SEGUIDOR_MUTATION,
  CriarSeguidorResponse,
} from '../../services/seguidorService'
import { dispatchGlobalError } from '../../context/ApiErrorContext'

interface ProfileCardTournamentProps {
  torneio?: TorneioProfile
  onAvatarUpdated?: (newUrl: string) => void
  onBannerUpdated?: (newUrl: string) => void
  onBannerVUpdated?: (newUrl: string) => void
  onSaveTorneio?: (torneio: TorneioProfile) => void
}

const ProfileCardTournament: React.FC<ProfileCardTournamentProps> = ({ torneio, onAvatarUpdated, onBannerUpdated, onBannerVUpdated, onSaveTorneio }) => {
  const auth = useAuth()
  const userObj =
    (auth?.user as any) ||
    (typeof window !== 'undefined' ? JSON.parse(localStorage.getItem('user') || 'null') : null)
  const seguidorUserId = userObj?.atleta?.id ?? '';
  const userId = userObj?.id;
  const isAdmin = userObj?.role === 'ADMIN';
  const isOrganizadorPermitido = torneio?.organizadores?.some(
    (org: Organizador) => org.usuario?.id === userId && org.nomeFuncao && ['ADMINISTRADOR', 'GERENTE'].includes(org.nomeFuncao)
  );
  const canEdit = isAdmin || isOrganizadorPermitido;
  

  const [seguir, { loading: loadingSeguir }] = useMutation<{
    criarSeguidor: CriarSeguidorResponse
  }, { input: any }>(CRIAR_SEGUIDOR_MUTATION)
  const [deletar, { loading: loadingDeletar }] = useMutation(DELETAR_SEGUIDOR_MUTATION)
  const [followId, setFollowId] = useState<string | null>(torneio?.seguidorId ?? null)
  const [erro, setErro] = useState('')
  const [avatarUrl, setAvatarUrl] = useState<string | null>(torneio?.avatarUrl || null)
  const [showModal, setShowModal] = useState(false)
  const [bannerUrl, setBannerUrl] = useState<string>(torneio?.bannerUrl || '/assets/images/comingsoon.svg')
  const [isBannerModalOpen, setIsBannerModalOpen] = useState(false)
  const [selectedBannerFile, setSelectedBannerFile] = useState<File | null>(null)
  const [previewBannerUrl, setPreviewBannerUrl] = useState<string | null>(null)
  const [isUploadingBanner, setIsUploadingBanner] = useState(false)
  const [bannerVUrl, setBannerVUrl] = useState<string | null>(torneio?.bannerVUrl || null)
  const [isBannerVModalOpen, setIsBannerVModalOpen] = useState(false)
  const [selectedBannerVFile, setSelectedBannerVFile] = useState<File | null>(null)
  const [previewBannerVUrl, setPreviewBannerVUrl] = useState<string | null>(null)
  const [isUploadingBannerV, setIsUploadingBannerV] = useState(false)
  const [editModeNome, setEditModeNome] = useState(false)
  const [editedNome, setEditedNome] = useState<string>(torneio?.nome || '')
  const [editModeUsername, setEditModeUsername] = useState(false)
  const [editedUsername, setEditedUsername] = useState<string>(torneio?.username || '')
  const [nomeTorneio, setNomeTorneio] = useState<string>(torneio?.nome || '')
  const [usernameTorneio, setUsernameTorneio] = useState<string>(torneio?.username || '')
  const [dataInicioTorneio, setDataInicioTorneio] = useState<string>(torneio?.dataInicio || '')
  const [dataFimTorneio, setDataFimTorneio] = useState<string>(torneio?.dataFim || '')
  const [dataInscricaoInicio, setDataInscricaoInicio] = useState<string>(torneio?.dataInscricaoInicio || '')
  const [dataInscricaoFim, setDataInscricaoFim] = useState<string>(torneio?.dataInscricaoFim || '')

  // Sincronizar estados quando a prop torneio mudar
  useEffect(() => {
    if (torneio) {
      setAvatarUrl(torneio.avatarUrl || null)
      setBannerUrl(torneio.bannerUrl || '/assets/images/comingsoon.svg')
      setBannerVUrl(torneio.bannerVUrl || null)
      setFollowId(torneio.seguidorId ?? null)
      setEditedNome(torneio.nome || '')
      setEditedUsername(torneio.username || '')
      setNomeTorneio(torneio.nome || '')
      setUsernameTorneio(torneio.username || '')
      setDataInicioTorneio(torneio.dataInicio || '')
      setDataFimTorneio(torneio.dataFim || '')
      setDataInscricaoInicio(torneio.dataInscricaoInicio || '')
      setDataInscricaoFim(torneio.dataInscricaoFim || '')
    }
  }, [torneio])

  const handleSalvarAlteracoes = async () => {
    onSaveTorneio?.({
      id: torneio?.id || '',
      nome: nomeTorneio,
      username: usernameTorneio,
      dataInicio: dataInicioTorneio,
      dataFim: dataFimTorneio,
      dataInscricaoInicio,
      dataInscricaoFim,
    });
  }

  const handleSaveAvatar = async (file: File) => {
    if (!torneio?.id) return
    if (!file.type.startsWith('image/')) {
      dispatchGlobalError('Apenas arquivos de imagem são permitidos.')
      throw new Error('invalid file type')
    }
    if (file.size > 5 * 1024 * 1024) {
      dispatchGlobalError('O arquivo deve ter no máximo 5MB.')
      throw new Error('file too large')
    }
    try {
      const url = await atualizarAvatarTorneio(torneio.id, file)
      setAvatarUrl(url)
      onAvatarUpdated?.(url)
      alert('Avatar atualizado com sucesso.')
    } catch (err) {
      throw err
    }
  }

  const closeBannerModal = () => {
    if (isUploadingBanner) return
    setIsBannerModalOpen(false)
    setSelectedBannerFile(null)
    if (previewBannerUrl) {
      URL.revokeObjectURL(previewBannerUrl)
      setPreviewBannerUrl(null)
    }
  }

  const handleBannerFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null
    if (!file) {
      setSelectedBannerFile(null)
      if (previewBannerUrl) {
        URL.revokeObjectURL(previewBannerUrl)
        setPreviewBannerUrl(null)
      }
      return
    }
    if (!file.type.startsWith('image/')) {
      dispatchGlobalError('Apenas arquivos de imagem são permitidos.')
      return
    }
    if (file.size > 5 * 1024 * 1024) {
      dispatchGlobalError('O arquivo deve ter no máximo 5MB.')
      return
    }
    setSelectedBannerFile(file)
    const url = URL.createObjectURL(file)
    setPreviewBannerUrl(url)
  }

  const handleBannerUpload = async () => {
    if (!torneio?.id || !selectedBannerFile) return
    setIsUploadingBanner(true)
    try {
      const { bannerUrl: newUrl } = await uploadBannerTorneio(torneio.id, selectedBannerFile)
      setBannerUrl(newUrl)
      onBannerUpdated?.(newUrl)
      closeBannerModal()
      alert('Banner atualizado com sucesso.')
    } catch (err) {
    } finally {
      setIsUploadingBanner(false)
    }
  }

  const closeBannerVModal = () => {
    if (isUploadingBannerV) return
    setIsBannerVModalOpen(false)
    setSelectedBannerVFile(null)
    if (previewBannerVUrl) {
      URL.revokeObjectURL(previewBannerVUrl)
      setPreviewBannerVUrl(null)
    }
  }

  const handleBannerVFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null
    if (!file) {
      setSelectedBannerVFile(null)
      if (previewBannerVUrl) {
        URL.revokeObjectURL(previewBannerVUrl)
        setPreviewBannerVUrl(null)
      }
      return
    }
    if (!file.type.startsWith('image/')) {
      dispatchGlobalError('Apenas arquivos de imagem são permitidos.')
      return
    }
    if (file.size > 5 * 1024 * 1024) {
      dispatchGlobalError('O arquivo deve ter no máximo 5MB.')
      return
    }
    setSelectedBannerVFile(file)
    const url = URL.createObjectURL(file)
    setPreviewBannerVUrl(url)
  }

  const handleBannerVUpload = async () => {
    if (!torneio?.id || !selectedBannerVFile) return
    setIsUploadingBannerV(true)
    try {
      const { bannerVUrl: newUrl } = await uploadBannerVTorneio(torneio.id, selectedBannerVFile)
      setBannerVUrl(newUrl)
      onBannerVUpdated?.(newUrl)
      closeBannerVModal()
      alert('Banner vertical atualizado com sucesso.')
    } catch (err) {
    } finally {
      setIsUploadingBannerV(false)
    }
  }

  async function handleNomeSave(event?: React.FocusEvent<HTMLInputElement>): Promise<void> {
    if (!torneio?.id || !editedNome.trim() || editedNome === torneio.nome) {
      setEditModeNome(false)
      return
    }
    setNomeTorneio(editedNome.trim());
    setEditModeNome(false);
  }

  function handleNomeCancel(): void {
    setEditModeNome(false);
    setEditedNome(torneio?.nome || '');
  }

  function handleUsernameSave(event?: React.FocusEvent<HTMLInputElement>): void {
    if (!torneio?.id || !editedUsername.trim() || editedUsername === torneio.username) {
      setEditModeUsername(false)
      return
    }
    setUsernameTorneio(editedUsername.trim());
    setEditModeUsername(false);
  }

  function handleUsernameCancel(): void {
    setEditModeUsername(false);
    setEditedUsername(torneio?.username || '');
  }

  return (
    <div className="card w-100 shadow-xss rounded-xxl border-0 mb-3 mt-3 overflow-hidden">
      <div
        id='banner-tournament'
        className="card-body position-relative h240 bg-image-cover bg-image-center"
        style={{ backgroundImage: `url("${bannerUrl}")` }}
      >
        <button
          type="button"
          onClick={() => setIsBannerModalOpen(true)}
          className="btn btn-dark btn-sm rounded-circle position-absolute d-flex align-items-center justify-content-center"
          style={{ bottom: '10px', right: '10px', opacity: 0.7 }}
          aria-label="Alterar banner do torneio"
          title="Alterar banner"
        >
          <i className="bi bi-camera text-white"></i>
        </button>
      </div>
      <div className="card-body d-block pt-4 position-relative">
        <figure
          id={canEdit ? 'avatar-container' : undefined}
          className="avatar mt--6 position-relative w75 z-index-1 me-auto"
          onClick={canEdit ? () => setShowModal(true) : undefined}
          aria-label={canEdit ? 'Alterar avatar do torneio' : undefined}
          style={{ cursor: canEdit ? 'pointer' : undefined }}
        >
          <img
            src={avatarUrl || '/assets/images/user.png'}
            alt={nomeTorneio || 'avatar'}
            className="p-1 bg-white rounded-xl w-100"
          />
          <div
            className="camera-overlay d-flex align-items-center justify-content-center"
            style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '100%',
            height: '100%',
            pointerEvents: 'none',
            }}
          >
            <i className="bi bi-camera fs-3"></i>
          </div>
        </figure>
        <div id='nome-tournament' className="font-xs ls-1 fw-700 text-grey-900 d-flex align-items-center">
          <div>
            {editModeNome ? (
              <input
                type="text"
                value={editedNome}
                onChange={e => setEditedNome(e.target.value)}
                className="form-control form-control-sm me-2"
                style={{ maxWidth: 200 }}
                onBlur={handleNomeSave}
                onKeyDown={e => {
                  if (e.key === 'Enter') handleNomeSave()
                  if (e.key === 'Escape') handleNomeCancel()
                }}
                autoFocus
              />
            ) : (
              <>
                {editedNome}
                <button
                  type="button"
                  className="btn btn-link p-0 ms-2"
                  style={{ lineHeight: 1 }}
                  aria-label="Editar nome do torneio"
                  onClick={() => {
                    setEditModeNome(true)
                  }}
                >
                <i className="bi bi-pencil fs-6"></i>
                </button>
              </>
            )}
            <div id='tag-tournament' className="d-block font-xssss fw-500 mt-1 lh-3 text-primary">

              {editModeUsername ? (
                <input
                  type="text"
                  value={editedUsername}
                  onChange={e => setEditedUsername(e.target.value)}
                  className="form-control form-control-sm me-2"
                  style={{ maxWidth: 200 }}
                  onBlur={handleUsernameSave}
                  onKeyDown={e => {
                    if (e.key === 'Enter') handleUsernameSave()
                    if (e.key === 'Escape') handleUsernameCancel()
                  }}
                  autoFocus
                />
              ) : (
                <>
                  {usernameTorneio ? `@${usernameTorneio}` : '@sem-username'}
                  <button
                    type="button"
                    className="btn btn-link p-0 ms-2"
                    style={{ lineHeight: 1 }}
                    aria-label="Editar username do torneio"
                    onClick={() => {
                      setEditModeUsername(true)
                    }}
                  >
                  <i className="bi bi-pencil fs-6"></i>
                  </button>
                </>
              )}
            </div>
            
          </div>
        </div>
            <div className="d-flex align-items-center justify-content-center position-absolute right-15 top-10 mt-2 me-2">
              <button
                onClick={handleSalvarAlteracoes}
                disabled={loadingSeguir || loadingDeletar}
                className={`d-none d-lg-block p-3 z-index-1 rounded-3 font-xssss text-uppercase fw-700 ls-3 btn ${followId ? 'btn-secondary' : 'btn-primary'}`}
              >
                SALVAR ALTERAÇÕES
              </button>
            </div>
            {erro && (
              <div className="alert alert-danger mt-2" role="alert">
                {erro}
              </div>
            )}
      </div>
      {/* Banner Vertical Section */}
      <div className="card-body border-top pt-3">
        <h6 className="fw-600 text-grey-900 font-xsss mb-3">Banner Vertical</h6>
        <div className="d-flex align-items-center gap-3">
          {bannerVUrl && (
            <img
              src={bannerVUrl}
              alt="Banner vertical do torneio"
              className="rounded-lg"
              style={{ maxHeight: 120, maxWidth: 80, objectFit: 'cover' }}
            />
          )}
          <button
            type="button"
            onClick={() => setIsBannerVModalOpen(true)}
            className="btn btn-outline-primary btn-sm d-flex align-items-center gap-2"
            aria-label="Alterar banner vertical do torneio"
          >
            <i className="bi bi-camera"></i>
            {bannerVUrl ? 'Alterar Banner Vertical' : 'Adicionar Banner Vertical'}
          </button>
        </div>
      </div>
      <UpdateAvatarModal
        open={showModal}
        onClose={() => setShowModal(false)}
        currentImage={avatarUrl || undefined}
        onSave={handleSaveAvatar}
      />
      {isBannerModalOpen && (
        <>
          <div className="modal-backdrop fade show" onClick={closeBannerModal} />
          <div className="modal fade show d-block" role="dialog" tabIndex={-1}>
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title w-100 text-center">Alterar banner do torneio</h5>
                  <button type="button" className="btn-close" aria-label="Fechar" onClick={closeBannerModal} disabled={isUploadingBanner} />
                </div>
                <div className="modal-body">
                  {previewBannerUrl && (
                    <img src={previewBannerUrl} alt="Prévia do banner" className="mb-3 w-100" style={{ maxHeight: 200, objectFit: 'cover' }} />
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleBannerFileChange}
                    disabled={isUploadingBanner}
                    className="form-control"
                  />
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={closeBannerModal} disabled={isUploadingBanner}>
                    Cancelar
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleBannerUpload}
                    disabled={!selectedBannerFile || isUploadingBanner}
                  >
                    {isUploadingBanner ? 'Enviando...' : 'Upload'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
      {isBannerVModalOpen && (
        <>
          <div className="modal-backdrop fade show" onClick={closeBannerVModal} />
          <div className="modal fade show d-block" role="dialog" tabIndex={-1}>
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title w-100 text-center">Alterar banner vertical do torneio</h5>
                  <button type="button" className="btn-close" aria-label="Fechar" onClick={closeBannerVModal} disabled={isUploadingBannerV} />
                </div>
                <div className="modal-body">
                  {previewBannerVUrl && (
                    <img src={previewBannerVUrl} alt="Prévia do banner vertical" className="mb-3 w-100" style={{ maxHeight: 300, objectFit: 'contain' }} />
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleBannerVFileChange}
                    disabled={isUploadingBannerV}
                    className="form-control"
                  />
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={closeBannerVModal} disabled={isUploadingBannerV}>
                    Cancelar
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleBannerVUpload}
                    disabled={!selectedBannerVFile || isUploadingBannerV}
                  >
                    {isUploadingBannerV ? 'Enviando...' : 'Upload'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export default ProfileCardTournament

